import {type Address} from '../cluster/Address.ts';
import {getByName, getNetworkInterfaces, type IpAddress} from '../utils/network.ts';
import { type AddressInfo, Server, createServer} from 'node:net';
import { type Logger } from '../logging.ts';
import { type Config} from '../config/Config.ts';

export interface AddressPicker {
    pickAddress(): Promise<void>;
    getPublicAddressFor(endpoint: 'member' | 'client'): Address ;
    getServers(): ReadonlyMap<string, Server>;
}

async function createAddress(addressDef: AddressDef, port: number): Promise<Address> {
    if (addressDef.host) {
        return {type: addressDef.ipAddress.family, host: addressDef.host, port, address: addressDef.ipAddress};
    }
    return {type: addressDef.ipAddress.family, host: await addressDef.ipAddress.getHost(), port, address: addressDef.ipAddress}
}

function createNetServer(logger: Logger, host: string, range: number | string): Promise<Server> {
    const ports = portRange(range);
    return new Promise((resolve, reject) => {
        let server = createServer({}, () => {});
        server.on('error', (err: Error) => {
            if (err['code'] === 'EADDRINUSE') {
                logger.debug(`port ${err['port']} already in use on address ${err['address']}`);
                const {value: port} = ports.next();
                if (port) {
                    logger.info(`retry starting server on port ${port} and host ${host ?? '<unspecified>'}`);
                    server.close();
                    server.listen(port, host);
                } else {
                    logger.warn(`all configured port(s) ${range} are in use. closing...`);
                    server.close();
                    reject(err);
                }
            } else {
                logger.error(`server error: ${err.message}`, err);
                reject(err);
            }
        });
        server.on('listening', () => {
            const info = server.address() as AddressInfo;
            logger.info(`listening on ${info.address}:${info.port}`);
            resolve(server);
        });
        try {
            const {value: port} = ports.next();
            server.listen(port, host);
        } catch (e) {
            logger.error(`error starting socket server`, e);
            reject(e instanceof Error ? e : new Error(`listen failed: ${e}`));
        }
    });
}

class InterfaceAddress {
    readonly address: string;
    readonly host?: string;
    constructor(address: string, host?: string) {
        this.address = address;
        this.host = host;
    }
}

class AddressDef extends InterfaceAddress {

    readonly ipAddress: IpAddress;
    readonly port: number;
    constructor(ipAddress: IpAddress, host?: string, port?: number) {
        super(ipAddress.address, host);
        this.ipAddress = ipAddress;
        this.port = port ?? 0;
    }
}

export class DefaultAddressPicker implements AddressPicker {

    readonly #logger: Logger;
    readonly #config: Config;
    readonly #publicAddressConfig?: string;
    #publicAddress: Address;
    #bindAddress: Address;
    private server: Server;
    readonly #port: string | number;
    constructor(config: Config,
                logger: Logger)
    {
        this.#logger = logger;
        this.#config = config;
        this.#port = config.network.port;
        this.#publicAddressConfig = config.network.publicAddress;
    }

    async pickAddress(): Promise<void> {
        if (this.#publicAddress || this.#bindAddress)
        {
            return;
        }
        const publicAddressDef = await this.getPublicAddressByPortSearch();
        this.#publicAddress = this.#bindAddress;

    }

    getPublicAddressFor(endpoint: 'member' | 'client'): Address {
        return this.#publicAddress;
    }

    getServers(): ReadonlyMap<string, Server> {
        return new Map([['member', this.server]]);
    }

    private async getPublicAddressByPortSearch() {
        const bindAddressDef = await this.pickAddressDef();


        this.server = await createNetServer(this.#logger, '0.0.0.0', bindAddressDef.port === 0 ? this.#port: bindAddressDef.port);

        const port = (this.server.address() as AddressInfo).port;
        this.#bindAddress = await createAddress(bindAddressDef, port);

        return this.getPublicAddress(port);
    }

    private async getPublicAddress(port: number) {
        let address = this.#publicAddressConfig;
        if (address) {
            address = address.trim();
            if (!address) {
                throw new Error('Public address is empty');
            } else if ('127.0.0.1' === address) {
                return this.pickLoopbackAddressDef(address, port);
            }
            else
            {
                const ipAddress = await getByName(address);
                return new AddressDef(ipAddress, address, port);
            }
        }
    }

    private async pickAddressDef() {
        let addressDef = this.pickInterfaceAddressDef();
        if (addressDef) {

        }
        if (addressDef === undefined) {
            addressDef = await this.pickLoopbackAddressDef();
        }
        return addressDef;
    }

    private pickInterfaceAddressDef(): AddressDef {
        const interfaces =  this.getInterfaces();
        if (interfaces) {
            const address = this.pickMatchingAddress(interfaces);
            if (address) {
                return address;
            }
        }
        return this.pickMatchingAddress();
    }

    private getInterfaces(): InterfaceAddress[] | undefined {
        return [];
    }

    private async pickLoopbackAddressDef(host?: string, defaultPort?: number) {
        const address = await getByName('127.0.0.1');
        return new AddressDef(address, host, defaultPort);
    }

    private match(ipAddress: IpAddress, interfaces: InterfaceAddress[]) {
        for (const iface of interfaces) {
            if (ipAddress.address === iface.address) {
                return iface;
            }
        }
        return undefined;
    }

    private getMatchingAddress(ipAddress: IpAddress, interfaces?: InterfaceAddress[]) {
        if (interfaces) {
            return this.match(ipAddress, interfaces);
        } else {
            return ipAddress;
        }
    }

    private pickMatchingAddress(interfaces?: InterfaceAddress[]): AddressDef | undefined {
        const preferIPv4 = true;
        const networkInterfaces = getNetworkInterfaces();
        let matchingAddress;
        for (const ni of networkInterfaces) {
            if (!interfaces && ni.loopback) {
                continue;
            }
            for (const ipAddress of ni.addresses) {
                if (preferIPv4 && ipAddress.family === 6) {
                    continue;
                }
                const address = this.getMatchingAddress(ipAddress, interfaces);
                break;
            }
        }
        return matchingAddress;
    }
}



// stolen from gateway-server


const PORT_RANGE_MATCHER = /^(\d+|(0x[\da-f]+))(-(\d+|(0x[\da-f]+)))?$/i;
function validPort(port: number) {
    if (port > 0xFFFF) throw new Error(`bad port ${port}`);
    return port;
}

/**
 * parse port range. port can be number or string representing comma separated
 *   list of port ranges for e.g. "3434,8380-8385"
 * @param port
 */
function* portRange(port: number | string): Generator<number> {
    if (typeof port === 'string') {
        for (const portRange of port.split(',')) {
            const trimmed = portRange.trim();
            const matchResult = PORT_RANGE_MATCHER.exec(trimmed);
            if (matchResult) {
                const start = parseInt(matchResult[1]);
                const end = parseInt(matchResult[4] ?? matchResult[1]);
                for (let i = validPort(start); i < validPort(end) + 1; i++) {
                    yield i;
                }
            }
            else {
                throw new Error(`'${portRange}' is not a valid port or range.`);
            }
        }
    } else {
        yield validPort(port);
    }
}
